import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  EmptyState, Flex, Input, Text
} from 'onefaceui-react-components';

import Loading from 'react/components/Loading';
import {
  checkServiceStatus,
  checkStopStatus,
  getDeliveryComplianceByStatusClass,
  getTWComplianceByStatusClass,
  getResourceTo
} from 'react/utils/utils';
import { useStopList } from 'react/queries/StopList';
import SearchIcon from 'static/images/monet/search_field.svg';
import {
  Container,
  Filters,
  SearchInput,
  ToggleButton,
  Header
} from './styles';
import StopListItem from './StopListItem';

const stopStatusDescriptions = {
  PENDING: 'Greenmile.Stop.IconDescription.pending',
  DELIVERED: 'Greenmile.Stop.IconDescription.completed',
  PARTIALLY_DELIVERED: 'Greenmile.Stop.IconDescription.partiallyDelivered',
  REDELIVERED: 'Greenmile.Stop.IconDescription.redelivery',
  UNDELIVERED: 'Greenmile.Stop.IconDescription.undeliverable',
  CANCELED: 'Greenmile.Stop.IconDescription.cancel',
  IN_PROGRESS: 'Greenmile.Stop.IconDescription.inProgress'
};

/**
 * StopList Component - Displays and filters a list of stops in the route monitor
 * @param {Object} props - Component props
 * @param {number} props.routeId - ID of the route to display stops for
 * @returns {JSX.Element} StopList component
 */
function StopList({ routeId }) {
  const { data: dataStops = [], isFetching } = useStopList(routeId);

  const [filters, setFilters] = useState({
    search: '',
    stop: {
      deliveryStatus: {
        pending: false,
        in_progress: false,
        delivered: false,
        partially_delivered: false,
        redelivered: false,
        undelivered: false,
        canceled: false
      }
    }
  });

  /**
   * Handle search input changes
   * @param {Object} event - Input change event
   */
  const handleChangeSearch = ({ target: { value } }) => {
    setFilters((prev) => ({ ...prev, search: value }));
  };

  /**
   * Toggle filter button for a specific status
   * @param {string} status - Stop status to toggle
   */
  const handleClickToggleBtn = (status) => {
    setFilters((prev) => ({
      ...prev,
      stop: {
        deliveryStatus: {
          ...prev.stop.deliveryStatus,
          [status]: !prev.stop.deliveryStatus[status]
        }
      }
    }));
  };

  /**
   * Filter stops based on search query and active status filters
   * @param {Array} stops - Stops to filter
   * @returns {Array} Filtered stops
   */
  const filterStops = useCallback(
    (stops) => {
      const {
        search,
        stop: { deliveryStatus }
      } = filters;

      const activeStatuses = Object.entries(deliveryStatus)
        .filter(([, active]) => active)
        .map(([status]) => status.toUpperCase());

      return stops.filter((stopItem) => {
        const { stop } = stopItem;

        const locationDesc = stop.location?.description
          ? stop.location.description.toLowerCase()
          : '';
        const addressLine = stop.location?.addressLine2
          ? stop.location.addressLine2.toLowerCase()
          : '';

        const matchesSearch = !search
          || locationDesc.includes(search.toLowerCase())
          || addressLine.includes(search.toLowerCase());

        const matchesStatus = !activeStatuses.length
          || activeStatuses.includes(stop.deliveryStatus);

        return matchesSearch && matchesStatus;
      });
    },
    [filters]
  );

  const processedStops = useMemo(() => filterStops(dataStops).map((stopList) => {
    const { stop } = stopList;
    const statusClass = checkStopStatus(stop);

    return {
      ...stopList,
      stop: {
        ...stop,
        statusClass,
        serviceStatus: checkServiceStatus(stop),
        deliveryCompliance: getDeliveryComplianceByStatusClass(statusClass),
        twCompliance: getTWComplianceByStatusClass(statusClass)
      }
    };
  }), [dataStops, filterStops]);

  const hasStops = !!processedStops.length;
  const showEmptyState = !hasStops && !isFetching;
  const showStopsList = hasStops && !isFetching;

  return (
    <Container flexDirection="column">
      <Header
        className="panel-route-header"
        flexDirection="column"
        gap={6}
        dataTestId="panel-route-stops"
      >
        <Flex flexDirection="column">
          <SearchInput>
            <Input
              placeholder={getResourceTo('modal.stopdetails.view.searchstops')}
              type="search"
              onChange={handleChangeSearch}
              dataTestId="search-input"
            />
            <img alt="Search Icon" src={SearchIcon} />
          </SearchInput>

          {/* Status filter buttons */}
          <Filters alignItems="center" gap={12} dataTestId="filters-content">
            <Text size="xs">
              {getResourceTo('monet.routemonitor.label.showonly')}
            </Text>
            <Flex alignItems="center" gap={6}>
              {Object.keys(filters.stop.deliveryStatus).map((status) => (
                <ToggleButton
                  key={status}
                  className={`${status} ${
                    filters.stop.deliveryStatus[status] ? 'active' : ''
                  }`}
                  type="button"
                  title={getResourceTo(
                    stopStatusDescriptions[status.toUpperCase()]
                  )}
                  onClick={() => handleClickToggleBtn(status)}
                  data-testid={`${status}-stop-btn`}
                />
              ))}
            </Flex>
          </Filters>
        </Flex>
      </Header>

      {/* Stop list */}
      <Flex
        flexDirection="column"
        data-testid="stops-list"
        className="stops-list"
        shouldShow={showStopsList}
      >
        {processedStops.map((stopList) => (
          <StopListItem
            key={stopList.stop.id}
            stop={stopList.stop}
            routeId={routeId}
            stopDescription={
              stopStatusDescriptions[stopList.stop.deliveryStatus]
            }
          />
        ))}
      </Flex>

      {/* Loading state */}
      <Flex
        alignItems="center"
        justifyContent="center"
        style={{ flex: 1 }}
        shouldShow={isFetching}
      >
        <Loading />
      </Flex>

      {/* Empty state */}
      <Flex
        flexDirection="column"
        justifyContent="center"
        style={{ flex: 1 }}
        shouldShow={showEmptyState}
      >
        <EmptyState />
      </Flex>
    </Container>
  );
}

StopList.propTypes = {
  routeId: PropTypes.number.isRequired
};

export default StopList;
